import React, {
  FC, KeyboardEvent, useCallback, useRef,
} from 'react';
import { PermMediaRounded, SendRounded } from '@material-ui/icons';
import styled from 'styled-components';
import ColoredMaterialIcon from './styled/ColoredMaterialIcon';
import { COLORS } from '../constants';
import ChatIconButton from './styled/ChatIconButton';
import useTwilioMessageSend from '../hooks/useTwilioMessageSend';
import useTwilioSendFile from '../hooks/useTwilioSendFile';
import useFirebaseChatConfig from '../hooks/useFirebaseChatConfig';
import ChatTextArea from './styled/ChatTextArea';

const ChatInputContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  width: 100%;
  background-color: ${COLORS.GREY_S};
  padding: 0 4px;
`;

const StyledFileInput = styled.input`
  display: none;
`;

const ChatInput: FC = () => {
  const { fileUpload } = useFirebaseChatConfig();
  const { sendMessage } = useTwilioMessageSend();
  const textAreaRef = useRef<HTMLTextAreaElement>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { FilePreviewComponent, onFileInput, onFilePaste } = useTwilioSendFile(fileInputRef, textAreaRef);

  const onMessageSend = useCallback(async () => {
    const $input = textAreaRef.current;
    if ($input) {
      const message = $input.value;
      if (!message.trim()) return;
      $input.value = '';
      $input.setAttribute('style', '');
      await sendMessage(message, {});
      $input.focus();
    }
  }, [sendMessage]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onMessageSend();
    }
  }, [onMessageSend]);

  const onFileBtnClick = useCallback(() => fileInputRef.current?.click(), [fileInputRef]);

  return (
    <>
      <ChatInputContainer id="chat-input-container">
        <StyledFileInput
          ref={fileInputRef}
          onChange={onFileInput}
          type="file"
        />
        {fileUpload && (
          <ChatIconButton id="chat-file-button" onClick={onFileBtnClick}>
            <ColoredMaterialIcon Icon={PermMediaRounded} />
          </ChatIconButton>
        )}
        <ChatTextArea
          id="chat-input-field"
          ref={textAreaRef}
          onKeyDown={onKeyDown}
          autoComplete="off"
          onPaste={fileUpload ? onFilePaste : undefined}
          rowsMax={5}
          autoFocus
        />
        <ChatIconButton id="chat-send-button" onClick={onMessageSend}>
          <SendRounded />
        </ChatIconButton>
      </ChatInputContainer>
      {FilePreviewComponent}
    </>
  );
};

export default React.memo(ChatInput);
